import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ThemingProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useStableCallback } from '@main/shared/browser';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';

import { useMemoObject } from '../hooks';

export interface ModalConfig {
  content: ReactNode;
  header?: ReactNode;
  size?: ThemingProps<'Modal'>['size'];
}

export type ModalContextProps = {
  openModal: (state: ModalConfig) => void;
  closeModal: () => void;
};

export type ModalStateContextProps = {
  isOpen: boolean;
};

const defaultConfig: ModalConfig = {
  size: 'xl',
  header: undefined,
  content: undefined,
};

const ModalContext = createContext<ModalContextProps>({
  openModal: () => ({}),
  closeModal: () => ({}),
});

const ModalStateContext = createContext<ModalStateContextProps>({
  isOpen: false,
});

export const useModal = () => useContext(ModalContext);
export const useModalState = () => useContext(ModalStateContext);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalState, setModalState] = useState<ModalConfig>(defaultConfig);
  const { header, content, ...modalProps } = modalState;
  const isOpenRef = useRef(false);

  const openModal = useStableCallback((newState: ModalConfig) => {
    const wasOpen = isOpenRef.current;
    isOpenRef.current = true;
    setModalState((prevState) => ({ ...(wasOpen ? prevState : defaultConfig), ...newState }));
    onOpen();
  });

  /* The config is left in place on close: resetting it here would snap a sized
   * modal back to the default size while its exit animation is still playing.
   * The next openModal starts from the defaults instead. */
  const closeModal = useStableCallback(() => {
    isOpenRef.current = false;
    onClose();
  });

  const contextValue = useMemoObject({ openModal, closeModal });
  const contextStateValue = useMemoObject({ isOpen });

  const renderHeader = () => {
    if (typeof header === 'string') {
      return <ModalHeader fontSize="lg">{header}</ModalHeader>;
    }
    return header;
  };

  return (
    <ModalContext.Provider value={contextValue}>
      <ModalStateContext.Provider value={contextStateValue}>
        {children}

        <Modal
          isOpen={isOpen}
          onClose={closeModal}
          isCentered={true}
          scrollBehavior="inside"
          motionPreset="slideInBottom"
          {...modalProps}
        >
          <ModalOverlay />
          <ModalContent
            /**
             * Following properties are added to add scrolling support for modal content:
             * There is a known issue in Chakra UI that prevents scrolling on modal if another Overlay element is present.
             * In our case, we may open a modal within a drawer, which prevents scrolling on modal.
             *
             * https://github.com/chakra-ui/chakra-ui/issues/7588
             * https://github.com/chakra-ui/chakra-ui/issues/8269
             */
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onScroll={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ModalCloseButton />
            {renderHeader()}
            {content}
          </ModalContent>
        </Modal>
      </ModalStateContext.Provider>
    </ModalContext.Provider>
  );
};
